"use client";

import { c as _c } from "react/compiler-runtime";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import TransferPanel from "@/components/nft-transfer/TransferPanel";
import { useTransfer } from "@/components/nft-transfer/TransferState";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { publicEnv } from "@/config/env";
import { CollectedCard, CollectedCollectionType, CollectionSeized, CollectionSort } from "@/entities/IProfile";
import { SortDirection } from "@/entities/ISort";
import { MEMES_SEASON } from "@/enums";
import { ApiIdentity } from "@/generated/models/ObjectSerializer";
import { areEqualAddresses } from "@/helpers/Helpers";
import { Page } from "@/helpers/Types";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import { fetchAllPages } from "@/services/6529api";
import { commonApiFetch } from "@/services/api/common-api";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import UserPageCollectedCards from "./cards/UserPageCollectedCards";
import { COLLECTED_COLLECTIONS_META, convertAddressToLowerCase } from "./filters/user-page-collected-filters.helpers";
import UserPageCollectedFilters from "./filters/UserPageCollectedFilters";
import UserPageCollectedFirstLoading from "./UserPageCollectedFirstLoading";
export interface ProfileCollectedFilters {
  readonly handleOrWallet: string;
  readonly accountForConsolidations: boolean;
  readonly collection: CollectedCollectionType | null;
  readonly seized: CollectionSeized | null;
  readonly szn: MEMES_SEASON | null;
  readonly page: number;
  readonly pageSize: number;
  readonly sortBy: CollectionSort;
  readonly sortDirection: SortDirection;
}
interface QueryUpdateInput {
  name: keyof typeof SEARCH_PARAMS_FIELDS;
  value: string | null;
}
const SEARCH_PARAMS_FIELDS = {
  address: "address",
  collection: "collection",
  seized: "seized",
  szn: "szn",
  page: "page",
  sortBy: "sort-by",
  sortDirection: "sort-direction"
} as const;
const SZN_TO_SEARCH_PARAMS: Record<MEMES_SEASON, string> = {
  [MEMES_SEASON.SZN1]: "1",
  [MEMES_SEASON.SZN2]: "2",
  [MEMES_SEASON.SZN3]: "3",
  [MEMES_SEASON.SZN4]: "4",
  [MEMES_SEASON.SZN5]: "5",
  [MEMES_SEASON.SZN6]: "6",
  [MEMES_SEASON.SZN7]: "7",
  [MEMES_SEASON.SZN8]: "8",
  [MEMES_SEASON.SZN9]: "9",
  [MEMES_SEASON.SZN10]: "10",
  [MEMES_SEASON.SZN11]: "11",
  [MEMES_SEASON.SZN12]: "12",
  [MEMES_SEASON.SZN13]: "13"
};
export default function UserPageCollected(t0) {
  const $ = _c(103);
  const {
    profile
  } = t0;
  const {
    address: connectedAddress
  } = useSeizeConnectContext();
  const isMobile = useIsMobileScreen();
  const defaultSortBy = CollectionSort.TOKEN_ID;
  const defaultSortDirection = SortDirection.DESC;
  const defaultSeized = CollectionSeized.SEIZED;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = useParams();
  const router = useRouter();
  let t1;
  if ($[0] !== params?.user) {
    t1 = params?.user?.toString().toLowerCase() ?? "";
    $[0] = params?.user;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const user = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = t3 => {
      const {
        seized,
        collection
      } = t3;
      if (!collection) {
        return defaultSeized;
      }
      if (!COLLECTED_COLLECTIONS_META[collection].filters.seized) {
        return defaultSeized;
      }
      if (!seized) {
        return null;
      }
      return Object.values(CollectionSeized).find(c => c === seized.toUpperCase()) ?? null;
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const convertSeized = t2;
  const convertSzn = _temp;
  const convertCollection = _temp2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = t4 => {
      const {
        sortBy,
        collection: collection_2
      } = t4;
      if (!sortBy) {
        return defaultSortBy;
      }
      if (collection_2 && !COLLECTED_COLLECTIONS_META[collection_2].filters.sort.includes(sortBy.toUpperCase() as CollectionSort)) {
        return defaultSortBy;
      }
      return Object.values(CollectionSort).find(c_1 => c_1 === sortBy.toUpperCase()) ?? defaultSortBy;
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const convertSortedBy = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = sortDirection => {
      if (!sortDirection) {
        return defaultSortDirection;
      }
      return Object.values(SortDirection).find(c_2 => c_2 === sortDirection.toUpperCase()) ?? defaultSortDirection;
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const convertSortDirection = t4;
  let t5;
  if ($[5] !== profile.handle || $[6] !== searchParams || $[7] !== user) {
    t5 = () => {
      const address = searchParams?.get(SEARCH_PARAMS_FIELDS.address);
      const collection_3 = searchParams?.get(SEARCH_PARAMS_FIELDS.collection);
      const seized_0 = searchParams?.get(SEARCH_PARAMS_FIELDS.seized);
      const szn_0 = searchParams?.get(SEARCH_PARAMS_FIELDS.szn);
      const page = searchParams?.get(SEARCH_PARAMS_FIELDS.page);
      const sortBy_0 = searchParams?.get(SEARCH_PARAMS_FIELDS.sortBy);
      const sortDirection_0 = searchParams?.get(SEARCH_PARAMS_FIELDS.sortDirection);
      const convertedAddress = convertAddressToLowerCase(address);
      const convertedCollection = convertCollection(collection_3 ?? null);
      return {
        handleOrWallet: convertedAddress ?? profile.handle ?? user,
        accountForConsolidations: !convertedAddress,
        collection: convertedCollection,
        seized: convertSeized({
          seized: seized_0 ?? null,
          collection: convertedCollection
        }),
        szn: convertSzn({
          szn: szn_0 ?? null,
          collection: convertedCollection
        }),
        page: page ? parseInt(page) : 1,
        pageSize: 24,
        sortBy: convertSortedBy({
          sortBy: sortBy_0 ?? null,
          collection: convertedCollection
        }),
        sortDirection: convertSortDirection(sortDirection_0 ?? null)
      };
    };
    $[5] = profile.handle;
    $[6] = searchParams;
    $[7] = user;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const getFilters = t5;
  let t6;
  if ($[9] !== searchParams) {
    t6 = updateItems => {
      const params_0 = new URLSearchParams(searchParams?.toString() ?? "");
      for (const {
        name,
        value
      } of updateItems) {
        const key = SEARCH_PARAMS_FIELDS[name];
        if (!value) {
          params_0.delete(key);
        } else {
          params_0.set(key, value.toLowerCase());
        }
      }
      return params_0.toString();
    };
    $[9] = searchParams;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const createQueryString = t6;
  let t7;
  if ($[11] !== createQueryString || $[12] !== pathname || $[13] !== router) {
    t7 = async updateItems_0 => {
      const queryString = createQueryString(updateItems_0);
      const path = queryString ? pathname + "?" + queryString : pathname;
      if (path) {
        router.replace(path, {
          scroll: false
        });
      }
    };
    $[11] = createQueryString;
    $[12] = pathname;
    $[13] = router;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const updateFields = t7;
  let t8;
  if ($[15] !== getFilters) {
    t8 = getFilters();
    $[15] = getFilters;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const [filters, setFilters] = useState(t8);
  const {
    enabled: transferEnabled
  } = useTransfer();
  let t9;
  if ($[17] !== filters.collection || $[18] !== filters.sortBy || $[19] !== updateFields) {
    t9 = async collection_4 => {
      if (!filters.collection && !collection_4) {
        return;
      }
      const items = [{
        name: "collection",
        value: filters.collection === collection_4 ? null : collection_4 ?? null
      }, {
        name: "page",
        value: "1"
      }, {
        name: "seized",
        value: defaultSeized
      }, {
        name: "szn",
        value: null
      }];
      if (collection_4 && !COLLECTED_COLLECTIONS_META[collection_4].filters.sort.includes(filters.sortBy)) {
        items.push({
          name: "sortBy",
          value: CollectionSort.TOKEN_ID
        });
        items.push({
          name: "sortDirection",
          value: SortDirection.DESC
        });
      }
      await updateFields(items);
    };
    $[17] = filters.collection;
    $[18] = filters.sortBy;
    $[19] = updateFields;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const setCollection = t9;
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = t11 => {
      const {
        newSortBy,
        currentSortBy,
        currentSortDirection
      } = t11;
      if (newSortBy === currentSortBy) {
        if (currentSortDirection === SortDirection.ASC) {
          return SortDirection.DESC;
        }
        return SortDirection.ASC;
      }
      return defaultSortDirection;
    };
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const calculateSortDirection = t10;
  let t11;
  if ($[22] !== filters.sortBy || $[23] !== filters.sortDirection || $[24] !== updateFields) {
    t11 = async sortBy_1 => {
      const items_0 = [{
        name: "sortBy",
        value: sortBy_1
      }, {
        name: "sortDirection",
        value: calculateSortDirection({
          newSortBy: sortBy_1,
          currentSortBy: filters.sortBy,
          currentSortDirection: filters.sortDirection
        })
      }, {
        name: "page",
        value: "1"
      }];
      await updateFields(items_0);
    };
    $[22] = filters.sortBy;
    $[23] = filters.sortDirection;
    $[24] = updateFields;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const setSortBy = t11;
  let t12;
  if ($[26] !== updateFields) {
    t12 = async seized_1 => {
      const items_1 = [{
        name: "seized",
        value: seized_1
      }, {
        name: "page",
        value: "1"
      }];
      await updateFields(items_1);
    };
    $[26] = updateFields;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const setSeized = t12;
  let t13;
  if ($[28] !== updateFields) {
    t13 = async szn_1 => {
      const items_2 = [{
        name: "szn",
        value: szn_1 ? SZN_TO_SEARCH_PARAMS[szn_1] : null
      }, {
        name: "page",
        value: "1"
      }];
      await updateFields(items_2);
    };
    $[28] = updateFields;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const setSzn = t13;
  let t14;
  if ($[30] !== updateFields) {
    t14 = async page_0 => {
      const items_3 = [{
        name: "page",
        value: page_0.toString()
      }];
      await updateFields(items_3);
    };
    $[30] = updateFields;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const setPage = t14;
  let t15;
  if ($[32] !== getFilters) {
    t15 = () => setFilters(getFilters());
    $[32] = getFilters;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== connectedAddress || $[35] !== getFilters || $[36] !== profile || $[37] !== searchParams || $[38] !== user) {
    t16 = [searchParams, profile, user, connectedAddress, getFilters];
    $[34] = connectedAddress;
    $[35] = getFilters;
    $[36] = profile;
    $[37] = searchParams;
    $[38] = user;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  useEffect(t15, t16);
  let t17;
  if ($[40] !== filters) {
    t17 = [QueryKey.PROFILE_COLLECTED, filters];
    $[40] = filters;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== filters.accountForConsolidations || $[43] !== filters.collection || $[44] !== filters.handleOrWallet || $[45] !== filters.page || $[46] !== filters.pageSize || $[47] !== filters.seized || $[48] !== filters.sortBy || $[49] !== filters.sortDirection || $[50] !== filters.szn) {
    t18 = async () => {
      const params_1 = {
        page: filters.page.toString(),
        page_size: filters.pageSize.toString(),
        sort: filters.sortBy.toLowerCase(),
        sort_direction: filters.sortDirection
      };
      if (!filters.accountForConsolidations) {
        params_1.account_for_consolidations = "false";
      }
      if (filters.collection) {
        params_1.collection = filters.collection;
      }
      if (filters.seized) {
        params_1.seized = filters.seized;
      }
      if (filters.szn) {
        params_1.szn = SZN_TO_SEARCH_PARAMS[filters.szn];
      }
      return await commonApiFetch({
        endpoint: `profiles/${filters.handleOrWallet}/collected`,
        params: params_1
      });
    };
    $[42] = filters.accountForConsolidations;
    $[43] = filters.collection;
    $[44] = filters.handleOrWallet;
    $[45] = filters.page;
    $[46] = filters.pageSize;
    $[47] = filters.seized;
    $[48] = filters.sortBy;
    $[49] = filters.sortDirection;
    $[50] = filters.szn;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== t17 || $[53] !== t18) {
    t19 = {
      queryKey: t17,
      queryFn: t18,
      placeholderData: keepPreviousData
    };
    $[52] = t17;
    $[53] = t18;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  const {
    isFetching,
    isLoading: isInitialLoading,
    data
  } = useQuery(t19);
  let t20;
  if ($[55] !== connectedAddress || $[56] !== data || $[57] !== isMobile || $[58] !== profile.wallets) {
    t20 = !isMobile && !!(profile.wallets?.some(w => areEqualAddresses(w.wallet, connectedAddress)) && data?.data && data.data.length > 0);
    $[55] = connectedAddress;
    $[56] = data;
    $[57] = isMobile;
    $[58] = profile.wallets;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  const showTransfer = t20;
  let t21;
  if ($[60] !== connectedAddress || $[61] !== filters) {
    t21 = [QueryKey.PROFILE_COLLECTED_TRANSFER, filters, connectedAddress];
    $[60] = connectedAddress;
    $[61] = filters;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== connectedAddress) {
    t22 = async () => {
      const allPagesUrl = `${publicEnv.API_ENDPOINT}/api/profiles/${connectedAddress}/collected?&page_size=200&seized=${CollectionSeized.SEIZED}`;
      const data_0 = await fetchAllPages(allPagesUrl);
      return data_0;
    };
    $[63] = connectedAddress;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  const t23 = showTransfer && transferEnabled;
  let t24;
  if ($[65] !== t21 || $[66] !== t22 || $[67] !== t23) {
    t24 = {
      queryKey: t21,
      queryFn: t22,
      enabled: t23,
      placeholderData: keepPreviousData
    };
    $[65] = t21;
    $[66] = t22;
    $[67] = t23;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  const {
    isFetching: isFetchingTransfer,
    data: dataTransfer
  } = useQuery(t24);
  const [totalPages, setTotalPages] = useState(1);
  let t25;
  if ($[69] !== data || $[70] !== filters.page || $[71] !== filters.pageSize || $[72] !== isFetching || $[73] !== setPage) {
    t25 = () => {
      if (isFetching) {
        return;
      }
      if (!data?.count) {
        setPage(1);
        setTotalPages(1);
        return;
      }
      const pagesCount = Math.ceil(data.count / filters.pageSize);
      if (pagesCount < filters.page) {
        setPage(pagesCount);
        return;
      }
      setTotalPages(pagesCount);
    };
    $[69] = data;
    $[70] = filters.page;
    $[71] = filters.pageSize;
    $[72] = isFetching;
    $[73] = setPage;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  const t26 = data?.count;
  const t27 = data?.page;
  let t28;
  if ($[75] !== isFetching || $[76] !== t26 || $[77] !== t27) {
    t28 = [t26, t27, isFetching];
    $[75] = isFetching;
    $[76] = t26;
    $[77] = t27;
    $[78] = t28;
  } else {
    t28 = $[78];
  }
  useEffect(t25, t28);
  let t29;
  if ($[79] !== filters.collection) {
    t29 = () => filters.collection ? COLLECTED_COLLECTIONS_META[filters.collection].showCardDataRow : true;
    $[79] = filters.collection;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  const getShowDataRow = t29;
  let t30;
  if ($[81] !== getShowDataRow) {
    t30 = getShowDataRow();
    $[81] = getShowDataRow;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  const [showDataRow, setShowDataRow] = useState(t30);
  let t31;
  if ($[83] !== getShowDataRow) {
    t31 = () => {
      setShowDataRow(getShowDataRow());
    };
    $[83] = getShowDataRow;
    $[84] = t31;
  } else {
    t31 = $[84];
  }
  let t32;
  if ($[85] !== filters.collection) {
    t32 = [filters.collection];
    $[85] = filters.collection;
    $[86] = t32;
  } else {
    t32 = $[86];
  }
  useEffect(t31, t32);
  const scrollContainer = useRef(null);
  let t33;
  if ($[87] !== data?.data || $[88] !== dataTransfer || $[89] !== filters || $[90] !== isFetchingTransfer || $[91] !== isInitialLoading || $[92] !== profile || $[93] !== setCollection || $[94] !== setPage || $[95] !== setSeized || $[96] !== setSortBy || $[97] !== setSzn || $[98] !== showDataRow || $[99] !== showTransfer || $[100] !== totalPages || $[101] !== transferEnabled) {
    t33 = <div className="tailwind-scope">{isInitialLoading ? <UserPageCollectedFirstLoading /> : <><div ref={scrollContainer}><UserPageCollectedFilters profile={profile} filters={filters} containerRef={scrollContainer} setCollection={setCollection} setSortBy={setSortBy} setSeized={setSeized} setSzn={setSzn} showTransfer={showTransfer} /></div><div className="tw-mt-6 tw-flex tw-gap-6"><UserPageCollectedCards cards={data?.data ?? []} totalPages={totalPages} page={filters.page} showDataRow={showDataRow} filters={filters} setPage={setPage} dataTransfer={dataTransfer ?? []} isTransferLoading={isFetchingTransfer} /></div>{showTransfer && transferEnabled && <TransferPanel isLoading={isFetchingTransfer} />}</>}</div>;
    $[87] = data?.data;
    $[88] = dataTransfer;
    $[89] = filters;
    $[90] = isFetchingTransfer;
    $[91] = isInitialLoading;
    $[92] = profile;
    $[93] = setCollection;
    $[94] = setPage;
    $[95] = setSeized;
    $[96] = setSortBy;
    $[97] = setSzn;
    $[98] = showDataRow;
    $[99] = showTransfer;
    $[100] = totalPages;
    $[101] = transferEnabled;
    $[102] = t33;
  } else {
    t33 = $[102];
  }
  return t33;
}
function _temp2(collection_1) {
  if (!collection_1) {
    return null;
  }
  return Object.values(CollectedCollectionType).find(c_0 => c_0 === collection_1.toUpperCase()) ?? null;
}
function _temp(t0) {
  const {
    szn,
    collection: collection_0
  } = t0;
  if (!collection_0) {
    return null;
  }
  if (!COLLECTED_COLLECTIONS_META[collection_0].filters.szn) {
    return null;
  }
  if (!szn) {
    return null;
  }
  const entry = Object.entries(SZN_TO_SEARCH_PARAMS).find(t1 => {
    const [, v] = t1;
    return v === szn;
  });
  return entry ? entry[0] as MEMES_SEASON : null;
}